/**
 * NEXORA AI - Company Step (Premium Refactor)
 * Epic 1.3 Phase 2.2 - Onboarding Premium
 * 
 * Émotion cible: PERSONNALISATION
 * Standards:
 * - Message de valorisation après validation
 * - Enrichissement visuel (icône + bénéfice + helper)
 * - Touch targets: 44x44px minimum
 * - Typography: H2 text-base md:text-lg
 * - Micro-interactions utiles (focus, validation)
 * - Responsive: Usage à une main validé
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, ArrowRight, ArrowLeft, Check, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export default function CompanyStep({ data, onNext, onBack, onData, currentStep, totalSteps }) {
  const { t } = useTranslation('onboarding');
  const [companyName, setCompanyName] = useState(data.company_name || '');
  const [website, setWebsite] = useState(data.company_website || '');
  const [showValidation, setShowValidation] = useState(false);

  const handleNext = () => {
    const hasData = companyName.trim().length > 0;
    
    onData({ 
      company_name: companyName.trim(), 
      company_website: website.trim() 
    });

    // Show validation message if user filled data
    if (hasData) {
      setShowValidation(true);
      // Delay to let user see the message
      setTimeout(() => {
        onNext();
      }, 1200);
    } else {
      onNext();
    }
  };

  const handleSkip = () => {
    onNext();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-2xl"
      >
        {/* Header with emotion */}
        <div className="text-center mb-8 sm:mb-10">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-blue-600/20 to-purple-600/20 border border-blue-500/30 mb-6"
          >
            <Building2 className="w-8 h-8 sm:w-10 sm:h-10 text-blue-400" strokeWidth={1.5} />
          </motion.div>

          <h2 className="text-2xl sm:text-3xl font-heading font-semibold text-white mb-3 sm:mb-4">
            {t('company_title')}
          </h2>
          <p className="text-base md:text-lg text-zinc-400 mb-2 max-w-xl mx-auto">
            {t('company_subtitle')}
          </p>
          
          {/* Benefit statement - Emotional hook */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex items-center justify-center gap-2 text-sm text-blue-400 mt-4"
          >
            <Info className="w-4 h-4 flex-shrink-0" />
            <span>{t('company_benefit')}</span>
          </motion.div>
        </div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="space-y-5 sm:space-y-6 mb-8"
        >
          {/* Company Name */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-zinc-300">
              {t('company_name_label')}
            </label>
            <Input
              type="text"
              placeholder={t('company_name_placeholder')}
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="h-12 bg-zinc-900/50 border-zinc-800 focus:border-blue-500 focus:ring-blue-500/20 text-white placeholder:text-zinc-500 transition-all duration-200"
              data-testid="company-name-input"
            />
          </div>

          {/* Website */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-zinc-300">
              {t('company_website_label')} <span className="text-zinc-500 font-normal">{t('company_optional')}</span>
            </label>
            <Input
              type="url"
              placeholder={t('company_website_placeholder')}
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="h-12 bg-zinc-900/50 border-zinc-800 focus:border-blue-500 focus:ring-blue-500/20 text-white placeholder:text-zinc-500 transition-all duration-200"
              data-testid="company-website-input"
            />
          </div>

          {/* Helper text */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-xs sm:text-sm text-zinc-500 text-center"
          >
            {t('company_helper')}
          </motion.p>
        </motion.div>

        {/* Validation Message - Appears after submission */}
        <AnimatePresence>
          {showValidation && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-6 p-4 rounded-xl bg-green-500/10 border border-green-500/30 flex items-center gap-3"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', duration: 0.3 }}
              >
                <Check className="w-5 h-5 text-green-400" />
              </motion.div>
              <p className="text-sm text-green-400 font-medium">
                {t('validation_company')}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation - Touch optimized */}
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            onClick={onBack}
            className="min-h-[44px] text-zinc-400 hover:text-white hover:bg-zinc-800"
            data-testid="company-back-button"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            <span className="hidden sm:inline">{t('back')}</span>
          </Button>

          <div className="flex gap-2 sm:gap-3">
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="min-h-[44px] text-zinc-400 hover:text-white"
              data-testid="company-skip-button"
            >
              {t('skip_step')}
            </Button>
            <Button
              variant="default"
              onClick={handleNext}
              disabled={showValidation}
              className="min-h-[44px] bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/20 transition-all duration-200"
              data-testid="company-continue-button"
            >
              {t('continue')}
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
